import React, { useEffect, useState, useRef, FunctionComponent, useMemo } from 'react';
import { connect, getLocale, history, useIntl, useRouteMatch } from 'umi';
import { Button, Card, Checkbox, ConfigProvider, Form, Input, Layout, Modal, notification, Radio, TagSelect } from 'tea-component';
import RangePicker from '@/pages/home/components/RangePicker';
import moment from 'moment';
import Abnormal from '../../components/Abnormal';
import TestModal from './components/TestModal';
import PushFrequency from './components/PushFrequency';
import { previewSubscribeSetting, saveSubscribeSetting } from '../../services/substribe';
import useConstants from './_config/constant';
import useFormField from './hooks/useFormField';
import useReceiverList from './hooks/useReceiverList';
import { formatUTC8Time, getEditFrequency, getFreq, getHourTime } from './_config/utils';
import { buriedBtnPoint } from '@/pages/home/utils/buriedPoint';
import { useBuriedPoint } from '@/pages/home/utils/customHook';
import { RANGE_DAILY_TYPE } from '../../components/NewFilter/FilterBase/_config';
import { LANGUAGE_CN } from '../../_config/constant';
import GuideSubscribe from '../../components/UserGuide/Subscribe';
import dateSvg from '@/pages/dashboard/assets/svg/date.svg';
import '@/pages/dashboard/assets/scss/page/Subscribe/subscribe.scss';
import '@/pages/dashboard/assets/scss/page/Subscribe/subscribeSetting.scss';
import { getPathInfo } from '../../utils/utils';
import BatchAddUserModal from './components/BatchAddUserModal';
import type {
  ISubscriptionFequencyProps,
  IQueryParams,
  IFrequencyParams,
  IRootState,
  IBatchAddModalInfo,
  IReceiverItem,
} from './subscribeSetting.types';

const { Content } = Layout;

const SubscriptionFequency: FunctionComponent<ISubscriptionFequencyProps> = (props) => {
  const { formatMessage } = useIntl();
  const url = useRouteMatch().url;
  const isZh = getLocale() === 'zh-CN';
  // subscribeOutSideEnable用来控制是否开启外部收件人（外部邮箱）
  const { gameCode, subscribeOutSideEnable: isUseOutsideEmail } = props;
  const query = (history?.location?.query ?? {}) as IQueryParams;
  // TODO:第一期不做筛选器
  // const {filterValue} = useSubscribeFilterValue({})
  const {
    SETTING_FIELDS,
    FREQUENCY_KEY_MAP,
    INITIAL_VALUES,
    WAYS,
    SENDOUTEMAILRADIO,
    SUBSCRIBE_SETTING_PAGE_REG
  } = useConstants({ formatMessage });

  const {
    backUrl,
    pageName,
    pageNameEn,
    pageNameCn,
    path,
    subscribeContent,
    pageType,
    formValue: formValueQuery = INITIAL_VALUES,
    isEdit,
    menuId
  } = query;
  const subscribeName = (pageName && isEdit ? pageName : isZh ? pageNameCn : pageNameEn) || '';
  // const isEditOutEamil = isOutsideUser === 1;
  // const isUseOutsideEmail = isEdit ? isEditOutEamil : subscribeOutSideEnable
  // 只要外部收件人开关打开了，那么内部用户重新订阅的时候是可以选择外部收件人的

  const [saveLoading, setSaveLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewError, setPreviewError] = useState<string>();
  const [previewLoading, setPreviewLoading] = useState(false);
  const [showSendOutEmailModal, setShowSendOutEmailModal] = useState(false);
  const [batchAddModalInfo, setBatchAddModalInfo] = useState<IBatchAddModalInfo>({ isVisible: false, type: '' });
  const composingRef = useRef(false);

  const handleTagSelectCompositionStartCapture = () => {
    composingRef.current = true;
  };

  const handleTagSelectCompositionEndCapture = () => {
    // setTimeout 兼容 Safari compositionend 时序问题
    setTimeout(() => {
      composingRef.current = false;
    }, 0);
  };

  const handleTagSelectKeyDownCapture = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const nativeEvent = e.nativeEvent as KeyboardEvent;

    if (e.key === 'Enter' && (composingRef.current || nativeEvent.isComposing)) {
      e.stopPropagation();
      e.preventDefault();
    }
  };

  const handleTagSelectBlurCapture = () => {
    composingRef.current = false;
  };
  const formVal = typeof formValueQuery === 'string' ? getEditFrequency(JSON.parse(formValueQuery)) : formValueQuery;
  const { receiverList } = useReceiverList({ gameCode, menuId, subscribeId: formVal?.id ?? '', });
  const formatFormVal = useMemo(() => {
    const recivers = typeof(formVal?.receivers)=== 'string' ? formVal?.receivers?.split(';') : formVal?.receivers;
    const receiversVal = recivers?.filter((value: string) => receiverList?.some((item: IReceiverItem) => item?.value === value));
    return {
      ...formVal,
      receivers: receiversVal
    };
  }, [formVal?.receivers, receiverList]);

  const {
    formValue,
    setFormValue,
    meta,
    input,
    hasErrors,
    handleSubmit,
    restForm,
  } = useFormField({
    formatMessage,
    receiverList,
    formValue: formatFormVal,
    isUseOutsideEmail
  });

  const isSelectOutEmail = useMemo(()=>isUseOutsideEmail && formValue?.[SETTING_FIELDS.SENDOUTEMAIL] === 'send',[isUseOutsideEmail, formValue, SETTING_FIELDS.SENDOUTEMAIL]);

  useEffect(() => {
    // 如果是从订阅管理页面跳转过来的, 不需要重置
    if (backUrl?.includes('/subscribe/subscribeManage')) return;
    // 仅更新了订阅报表设置页面的URL时, 对表单进行重置
    // 例如: 在 A报表的订阅设置页面, 点击B报表的订阅设置按钮, 那么重置表单
    if (backUrl?.includes("/subscribe/subscribeSetting")) {
      restForm();
    }
  }, [query]);

  // 打点信息
  const logObj = {
    pageId: '202501',
    pageName: 'subscribeSetting',
    extInfo: 'subscribeSetting',
    pagePath: '/subscribe/subscribeSetting',
    uid: gameCode,
    uidType: 'game_code'
  };
  useBuriedPoint(logObj);

  const toSubscribePage = () => {
    const subscribePath = getSubscribePath() ?? subscribeContent;
    // 订阅成功后跳转到订阅报表页
    if (subscribePath) {
      history?.replace(subscribePath?.replace(`${location.origin}/dashboard`, ''));
    }
  };

  const getSubscribePath = () => {
    if (!path) return null;
    //  encodeURI 兜底转义空格，正常应该没有空格
    return `${location.origin}/v2${getPathInfo().basePath}${encodeURI(path)}`; 

  };
  const getParams = () => {
    const { receivers, title, content, ways, frequency, cycle, viewport_width } = formValue || {};

    const reciversVal = typeof(receivers)=== 'string' ? receivers?.split(';') : receivers;

    const receiversArr = reciversVal?.filter((item: string) => item !== '');
    const { type } = frequency || {};
    const frequencyKeys = FREQUENCY_KEY_MAP?.[type];
    const frequncyParams: IFrequencyParams = {
      frequency_time: '',
      frequency_interval: ''
    };
    frequencyKeys?.forEach((key: string) => {
      if (key === 'frequency_interval' && typeof frequency[key] !== 'string') {
        frequncyParams[key] = frequency[key]?.join(',');
      } else {
        frequncyParams[key] = frequency[key];
      }
    });

    const { freqDay, freqTime } = getFreq(frequncyParams, type);
    return {
      game_code: gameCode,
      subscribe_id: formValue?.id ?? '',
      subscribe_title: title,
      subscribe_name: subscribeName,
      subscribe_content: getSubscribePath() ?? subscribeContent,
      receiver_account: receiversArr?.join(','),
      receiver_user_ids: !isSelectOutEmail ? receiversArr?.join(',') : '', // 后端要求新增receiver_user_ids 字段.
      subscribe_desc: content,
      send_type: ways?.join(',') || '',
      frequency_type: Number(type),
      frequency_time: getHourTime(freqTime),
      frequency_interval: freqDay,
      subscribe_status: 1,
      frequency_crontab_format: '',
      // cycle?.[0]是英文模式，所以要转一下
      start_date: formatUTC8Time(`${moment(cycle?.[0]).format('YYYY-MM-DD')} 00:00:00`),
      end_date: formatUTC8Time(`${moment(cycle?.[1]).format('YYYY-MM-DD')} 23:59:59`),
      page_type: pageType,
      is_outside_user: isSelectOutEmail ? 1 : 0,
      ...(viewport_width ? { viewport_width: Number(viewport_width) } : {}),
      // receiver_email:isSelectOutEmail ? receiversArr?.join(',') : '',
    };
  };

  // 保存
  const handleSave = () => {
    if (hasErrors) return;
    if (isSelectOutEmail) {
      setShowSendOutEmailModal(true);
      return;
    }
    saveSubscribe();
  };


  const saveSubscribe = () => {
    const params = getParams();
    setSaveLoading(true);
    buriedBtnPoint({
      buttonId: `${logObj.pageId  }001`,
      extInfo: JSON.stringify(params),
      pageId: logObj.pageId
    });
    saveSubscribeSetting(
      {
        ...params,
        menu_id: menuId || '',
      }
    )
      .then(() => {
        
        notification.success({
          description: (
            <span>
              {
                formatMessage({ id: 'subscribe_setting_save_success1' })
              }&nbsp;
              <a
                onClick={() => history?.replace(url?.replace(SUBSCRIBE_SETTING_PAGE_REG, '/subscribe/subscribeManage'))}
              >
                {
                  formatMessage({ id: 'subscribe_manager_page' })
                }
              </a>&nbsp;
              {
                formatMessage({ id: 'subscribe_setting_save_success2' })
              }
            </span>
          )
        });
        toSubscribePage();
      })
      .catch((err) => {
        console.log(err);
        notification.error({
          description: (
            <span>
              {formatMessage({ id: 'subscribe_setting_save_fail' })}
            </span>
          )
        });
      })
      .finally(() => {
        setSaveLoading(false);
      });
  };

  // 预览
  const handlePreview = () => {
    if (hasErrors) return;
    const params = getParams();

    buriedBtnPoint({
      buttonId: `${logObj.pageId  }002`,
      extInfo: JSON.stringify(params),
      pageId: logObj.pageId
    });

    setPreviewLoading(true);
    setPreviewError(undefined);
    setPreviewVisible(true);
    previewSubscribeSetting(params)
      .then((res) => {
        const url = res?.picture_url;
        if (url) {
          const imgObj = new Image();
          imgObj.src = url;
          imgObj.onload = function () {
            setPreviewUrl(url);
            setPreviewLoading(false);
          };
        } else {
          setPreviewError(`${subscribeName}${formatMessage({ id: 'subscribe_setting_prevew_fail_msg' })}`);
          setPreviewLoading(false);
        }
      })
      .catch((err) => {
        console.log(err);
        setPreviewLoading(false);
        setPreviewError(`${subscribeName}${formatMessage({ id: 'subscribe_setting_prevew_fail' })}! ${err.code ? `${formatMessage({ id: 'error_code_error_code' })}${err?.code}` : ''}`);
      });
  };

  // 测试发送
  const handleTest = (e: { preventDefault: () => void; stopPropagation: () => void; }) => {
    e.preventDefault();
    e.stopPropagation();
    setModalVisible(true);
  };

  // 取消
  const handleCancel = (e?: { preventDefault: () => void; stopPropagation: () => void; }) => {
    e?.preventDefault();
    e?.stopPropagation();
    buriedBtnPoint({
      buttonId: `${logObj.pageId  }004`,
      extInfo: '订阅取消',
      pageId: logObj.pageId
    });
    // 订阅管理过来取消点击返回，其他情况下挑战到订阅的报表页
    if (backUrl) {
      history?.replace(backUrl);
    } else {
      toSubscribePage();
    }
  };

  const handleBatchAddConfirm = (mappedValues: (string | number)[]) => {
    setBatchAddModalInfo({ isVisible: false, type: '' });
    // 将新的值合并到现有的 receivers 中
    const currentReceivers = formValue?.[SETTING_FIELDS.RECEIVERS] || [];
    // 去重
    const newReceivers = [...new Set([...currentReceivers, ...mappedValues])];
    
    setFormValue({
      ...formValue,
      [SETTING_FIELDS.RECEIVERS]: newReceivers
    });
  };

  return (
    <>
      <Content className={`dbd-subscribesetting ${isUseOutsideEmail ? 'dbd-subscribesetting-out-email' : ''}`}>
        <Content.Header
          title={formatMessage({ id: 'subscribe_setting_title' })}
          showBackButton
          onBackButtonClick={handleCancel}
        >
        </Content.Header>
        <ConfigProvider locale={getLocale() === 'zh-CN' ? 'zh' : "en"}>
          <Content.Body full>
            <Card className='dbd-subscribesetting-form'>
              <Card.Body>
                <form onSubmit={handleSubmit}>
                  <ConfigProvider locale={getLocale() === 'zh-CN' ? 'zh' : "en"}>
                    <Form className='dbd-subscribesetting-form-table'>
                      <Form.Item className='form-item dbd-form-item-title' label={formatMessage({ id: 'subscribe_setting_input_label_page' })}>
                        <div className='form-exit'>
                          <div className='form-exit-title'>{subscribeName}</div>
                          <div className='form-exit-text'>{formatMessage({ id: 'subscribe_setting_input_page_des' })}</div>
                        </div>
                      </Form.Item>
                      {
                        isUseOutsideEmail && <Form.Item
                          className='form-item dbd-form-item-title'
                          label={formatMessage({ id: 'subscribe_setting_input_label_out_email' })}
                          required
                          status={meta?.[SETTING_FIELDS.SENDOUTEMAIL]?.status}
                        >
                          <Radio.Group  {...input?.[SETTING_FIELDS.SENDOUTEMAIL]}>
                            {
                              SENDOUTEMAILRADIO?.map(({ name, value }) => <Radio key={value} name={value}>{name}</Radio>)
                            }
                          </Radio.Group>
                        </Form.Item>
                      }

                      <Form.Item
                        className='form-item dbd-form-item-recipients dbd-form-item-htop'
                        label={formatMessage({ id: 'subscribe_setting_input_label_receiver' })}
                        required
                        status={meta?.[SETTING_FIELDS.RECEIVERS]?.status}
                        message={meta?.[SETTING_FIELDS.RECEIVERS]?.errorMessage}
                        extra={(formValue?.[SETTING_FIELDS.SENDOUTEMAIL] === 'no' || !isUseOutsideEmail) && 
                          <Button 
                            type='link' 
                            onClick={(e?: React.MouseEvent) => {
                              e?.preventDefault();
                              setBatchAddModalInfo({ isVisible: true, type: ''});
                            }}
                          >
                            {`+ ${formatMessage({id: 'subscribe_batch_add'})}`}
                          </Button>
                        }
                      >
                        {
                          (formValue?.[SETTING_FIELDS.SENDOUTEMAIL] === 'send' && isUseOutsideEmail) ? <Input
                            {...input?.[SETTING_FIELDS.RECEIVERS]}
                            placeholder={formatMessage({ id: 'subscribe_setting_input_label_out_email_placeholder' })}
                          /> : <div
                            className='dbd-subscription-setting-tag-select-wrapper'
                            onCompositionStartCapture={handleTagSelectCompositionStartCapture}
                            onCompositionEndCapture={handleTagSelectCompositionEndCapture}
                            onKeyDownCapture={handleTagSelectKeyDownCapture}
                            onBlurCapture={handleTagSelectBlurCapture}
                          >
                            <TagSelect
                              optionsOnly
                              className='form-item-tagselect dbd-subscription-setting-tag-select'
                              placeholder={formatMessage({ id: 'subscribe_setting_input_placeholder_receiver' })}
                              options={receiverList}
                              {...input?.[SETTING_FIELDS.RECEIVERS]}
                            />
                          </div>
                        }

                      </Form.Item>
                      <Form.Item
                        className='form-item dbd-form-item-htop'
                        label={formatMessage({ id: 'subscribe_setting_input_label_title' })}
                        required
                        status={meta?.[SETTING_FIELDS.TITLE]?.status}
                        message={meta?.[SETTING_FIELDS.TITLE]?.errorMessage}
                      >
                        <Input
                          {...input?.[SETTING_FIELDS.TITLE]}
                          placeholder={formatMessage({ id: 'subscribe_setting_input_placeholder_title' })}
                        />
                      </Form.Item>
                      <Form.Item
                        className='form-item dbd-form-item-desc'
                        label={formatMessage({ id: 'subscribe_setting_input_label_content' })}
                      >
                        <Input
                          multiline
                          placeholder={formatMessage({ id: 'subscribe_setting_input_placeholder_content' })}
                          {...input?.[SETTING_FIELDS.CONTENT]}
                          maxLength={1000}
                        />
                      </Form.Item>
                      <Form.Item
                        className='form-item'
                        label={formatMessage({ id: 'subscribe_setting_input_label_ways' })}
                        required
                        status={meta?.[SETTING_FIELDS.WAYS]?.status}
                        message={meta?.[SETTING_FIELDS.WAYS]?.errorMessage}
                      >
                        <Checkbox.Group
                          {...input?.[SETTING_FIELDS.WAYS]}
                        >
                          {
                            WAYS?.map(({ name, value }) => <Checkbox disabled={isSelectOutEmail && value=== '2'} key={value} name={value}>{name}</Checkbox>)
                          }
                        </Checkbox.Group>
                      </Form.Item>

                      <Form.Item
                        className='form-item dbd-form-item-htop'
                        label={formatMessage({ id: 'subscribe_setting_viewport_width', defaultMessage: '页面宽度' })}
                      >
                        <Input
                          {...input?.[SETTING_FIELDS.VIEWPORT_WIDTH]}
                          placeholder={formatMessage({ id: 'subscribe_setting_viewport_width_placeholder', defaultMessage: '请输入截图页面宽度，默认2600' })}
                          autoComplete="off"
                        />
                      </Form.Item>
                      <Form.Item
                        className='form-item dbd-form-item-htop'
                        label={formatMessage({ id: 'subscribe_setting_input_label_frequency' })}
                        required
                        status={meta?.[SETTING_FIELDS.FREQUENCY]?.status}
                        message={meta?.[SETTING_FIELDS.FREQUENCY]?.errorMessage}
                      >
                        <PushFrequency
                          {...input?.[SETTING_FIELDS.FREQUENCY]}
                        />
                      </Form.Item>
                      <Form.Item
                        className='form-item dbd-form-item-htop'
                        label={formatMessage({ id: 'subscribe_setting_input_label_circle' })}
                        required
                        status={meta?.[SETTING_FIELDS.CYCLE]?.status}
                        message={meta?.[SETTING_FIELDS.CYCLE]?.errorMessage}
                      >
                        <div className='dbd-rangetime-picker-box'>
                          {
                            ['1', '2', 1, 2].includes(formValue?.[SETTING_FIELDS.FREQUENCY]?.type) ? (
                              <div className='dbd-rangetime-picker-disabled'>
                                <img
                                  className='dbd-time-icon'
                                  src={dateSvg}
                                />
                                <p className='dbd-time-text'>{formatMessage({ id: 'subscribe_setting_range_time_day' })}&nbsp;&nbsp;&nbsp;</p>
                                <p className='dbd-time-text'>{formatMessage({ id: 'subscribe_setting_range_time_text' })}</p>
                              </div>
                            ) : (
                              <div className='subscribemanage-search search-item search-item-date subscribemanage-search-record'>
                                <RangePicker
                                  type='date'
                                  className=""
                                  typeList={RANGE_DAILY_TYPE}
                                  showSelectType={false}
                                  showShortcut={false}
                                  range={[moment(), moment().add(6, 'month')]}
                                  language={getLocale() === LANGUAGE_CN ? 'cn' : 'en'}
                                  {...input?.[SETTING_FIELDS.CYCLE]}
                                />
                              </div>
                            )
                          }
                        </div>
                      </Form.Item>
                    </Form>
                  </ConfigProvider>
                  <Form.Action>
                    <div className='dbd-subscribesetting-btn'>
                      <Button
                        type='primary'
                        htmlType='submit'
                        loading={saveLoading}
                        onClick={handleSave}
                      >
                        {formatMessage({ id: 'subscribe_setting_btn_save' })}
                      </Button>
                      {/* 关掉弹窗，则loading也去掉 */}
                      <Button loading={previewLoading && previewVisible} onClick={handlePreview} htmlType='submit'>{formatMessage({ id: 'subscribe_setting_btn_preview' })}</Button>
                      <Button onClick={handleTest} disabled={!formValue?.title}>{formatMessage({ id: 'subscribe_setting_btn_test' })}</Button>
                      {/* <Button onClick={handleCancel}>{formatMessage({ id: 'subscribe_setting_btn_cancel' })}</Button> */}
                    </div>
                  </Form.Action>
                  <TestModal
                    key={Number(modalVisible)}
                    isSelectOutEmail={isSelectOutEmail}
                    isVisible={modalVisible}
                    menuId={menuId}
                    // gameCode={gameCode}
                    // pageName={pageName}
                    params={getParams()}
                    receiverList={receiverList}
                    close={() => setModalVisible(false)}
                  />
                </form>
              </Card.Body>
            </Card>

            <Modal
              visible={previewVisible}
              onClose={() => setPreviewVisible(false)}
              caption={subscribeName}
              className="dbd-subscription-setting-preview-modal"
              destroyOnClose={false}
            >
              <Modal.Body>
                <Abnormal
                  style={{ width: '100%', minHeight: '68vh', display: 'flex', alignItem: 'center' }}
                  isLoading={previewLoading}
                  isError={!!previewError}
                  failedMsg={previewError}
                  component={(<img className='dbd-subscribe-preview-img' src={previewUrl} alt={`${subscribeName}预览图片`} />)}
                />

              </Modal.Body>
            </Modal>

            <Modal
              visible={showSendOutEmailModal}
              caption={formatMessage({ id: 'subscribe_setting_btn_sennd_modal_confirm' })}
              onClose={() => setShowSendOutEmailModal(false)}>
              <Modal.Body>
                <div className="deleteSetting-desc">{formatMessage({ id: 'subscribe_setting_confirm_send_out_email' })}</div>
              </Modal.Body>
              <Modal.Footer>
                <Button type="primary" loading={saveLoading} onClick={saveSubscribe}>
                  {formatMessage({ id: 'common_confirm' })}
                </Button>
                <Button type="weak" onClick={() => setShowSendOutEmailModal(false)}>
                  {formatMessage({ id: 'common_cancel' })}
                </Button>
              </Modal.Footer>
            </Modal>
            <BatchAddUserModal 
              batchAddModalInfo={batchAddModalInfo}
              onClose={() => setBatchAddModalInfo({ isVisible: false, type: '' })}
              onConfirm={handleBatchAddConfirm}
              receiverList={receiverList}
            ></BatchAddUserModal>
            <GuideSubscribe />
          </Content.Body>
        </ConfigProvider>
      </Content>
    </>
  );
};

export default connect((state: IRootState) => ({
  gameCode: state.dashboard?.currentGame?.game_code ?? '',
  subscribeOutSideEnable: state?.dashboardSetting?.gameConfig?.subscribe_out_side_enable ?? false
}))(SubscriptionFequency);
